import { Link } from 'react-router-dom';
import { companyInfo, products, DOZZ_LOGO_URL, CATALOGUE_URL } from '../../data/mock';
import { MapPin, Phone, Mail, ArrowUpRight, ChevronRight } from 'lucide-react';

const quickLinks = [
  { label: 'About Us', href: '/#about' },
  { label: 'Our Products', href: '/#products' },
  { label: 'Our Brands', href: '/#brands' },
  { label: 'Certifications', href: '/#certifications' },
  { label: 'Our Clients', href: '/#clients' },
  { label: 'Request Quote', href: '/#inquiry' },
];

const topProducts = products.slice(0, 6);

export const Footer = () => {
  const handleLinkClick = (href) => {
    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '');
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-navy-950 text-navy-200 relative overflow-hidden">
      {/* Decorative top border */}
      <div className="h-1 bg-gradient-to-r from-ind-red-600 via-ind-red-500 to-ind-red-600" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img src={DOZZ_LOGO_URL} alt="DOZZ Logo" className="h-10 w-auto rounded-md" />
              <div>
                <p className="font-heading font-bold text-white text-lg">{companyInfo.name}</p>
              </div>
            </div>
            <p className="text-sm text-navy-300 mb-5 leading-relaxed">
              {companyInfo.tagline}. ISO 9001:2015 certified manufacturer since {companyInfo.established}.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-ind-red-400 flex-shrink-0" />
                <span className="text-sm text-navy-300">{companyInfo.address.full}</span>
              </div>
              {companyInfo.phones.map((phone) => (
                <a key={phone} href={`tel:${phone}`} className="flex items-center gap-2.5 text-sm text-navy-300 hover:text-white">
                  <Phone className="w-4 h-4 text-ind-red-400 flex-shrink-0" />
                  {phone}
                </a>
              ))}
              {companyInfo.emails.map((email) => (
                <a key={email} href={`mailto:${email}`} className="flex items-center gap-2.5 text-sm text-navy-300 hover:text-white">
                  <Mail className="w-4 h-4 text-ind-red-400 flex-shrink-0" />
                  {email}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-white text-base mb-5">Quick Links</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => handleLinkClick(link.href)}
                    className="text-sm text-navy-300 hover:text-ind-red-400 flex items-center gap-1.5 group"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-navy-600 group-hover:text-ind-red-400" />
                    {link.label}
                  </button>
                </li>
              ))}
              <li>
                <a href={CATALOGUE_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-navy-300 hover:text-ind-red-400 flex items-center gap-1.5 group">
                  <ChevronRight className="w-3.5 h-3.5 text-navy-600 group-hover:text-ind-red-400" />
                  Download Catalogue
                </a>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-heading font-bold text-white text-base mb-5">Our Products</h4>
            <ul className="space-y-2.5">
              {topProducts.map((product) => (
                <li key={product.slug}>
                  <Link
                    to={`/products/${product.slug}`}
                    className="text-sm text-navy-300 hover:text-ind-red-400 flex items-center gap-1.5 group"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-navy-600 group-hover:text-ind-red-400" />
                    {product.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Certifications & CTA */}
          <div>
            <h4 className="font-heading font-bold text-white text-base mb-5">Certifications</h4>
            <div className="flex flex-wrap gap-2 mb-6">
              {['ISO 9001:2015', 'BIS', 'ERDA', 'CPRI', 'NTH'].map((cert) => (
                <span
                  key={cert}
                  className="text-xs font-semibold bg-navy-800 text-navy-200 px-3 py-1.5 rounded-md border border-navy-700"
                >
                  {cert}
                </span>
              ))}
            </div>
            <div className="bg-navy-900/60 border border-navy-800 rounded-xl p-5">
              <p className="text-sm font-medium text-white mb-3">Need Industrial Pricing?</p>
              <button
                onClick={() => handleLinkClick('/#inquiry')}
                className="w-full bg-ind-red-600 hover:bg-ind-red-700 text-white text-sm font-semibold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2"
              >
                Send BOQ for Immediate Pricing
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-8 border-t border-navy-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-navy-500">
              &copy; {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
            <p className="text-xs text-navy-500 text-center md:text-right">
              Technical specifications may change as per continuous research and development.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
